import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Users } from 'lucide-react';
import { initials } from '@/lib/format';

/** In-memory blob URL cache for WhatsApp avatars (path → object URL). */
const avatarUrlCache = new Map();

/**
 * Circular avatar: WhatsApp profile photo or initials / group icon.
 * path = lead.wa_avatar (media relative path)
 */
export default function WaAvatar({
  path,
  name = '',
  isGroup = false,
  size = 40,
  className = '',
}) {
  const [url, setUrl] = useState(() => (path && avatarUrlCache.get(path)) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      setFailed(false);
      return undefined;
    }
    if (avatarUrlCache.has(path)) {
      setUrl(avatarUrlCache.get(path));
      setFailed(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    setFailed(false);
    api
      .get(`/whatsapp/media/file?path=${encodeURIComponent(path)}`, { responseType: 'blob' })
      .then((r) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        avatarUrlCache.set(path, objectUrl);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setUrl(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  const dim = typeof size === 'number' ? `${size}px` : size;
  const fontSize = typeof size === 'number' ? Math.max(11, Math.round(size * 0.32)) : '12px';

  if (url && !failed) {
    return (
      <img
        src={url}
        alt=""
        className={`rounded-full object-cover shrink-0 bg-muted ${className}`}
        style={{ width: dim, height: dim }}
        onError={() => setFailed(true)}
      />
    );
  }

  if (isGroup) {
    return (
      <div
        className={`rounded-full shrink-0 grid place-items-center bg-emerald-600/15 text-emerald-700 ${className}`}
        style={{ width: dim, height: dim }}
        title="Grupo de WhatsApp"
      >
        <Users style={{ width: Math.round((typeof size === 'number' ? size : 40) * 0.45), height: Math.round((typeof size === 'number' ? size : 40) * 0.45) }} />
      </div>
    );
  }

  return (
    <div
      className={`rounded-full shrink-0 grid place-items-center bg-accent text-foreground font-semibold ${className}`}
      style={{ width: dim, height: dim, fontSize }}
    >
      {initials(name || '?') || '?'}
    </div>
  );
}
